import type { Conditions } from '@flipster/styled-system/types';

import { breakpoints } from '../../panda/tokens/layout';

export type BreakpointConditions = Extract<
  keyof Conditions,
  | `${keyof typeof breakpoints}Down`
  | `${keyof typeof breakpoints}Only`
  | `${keyof typeof breakpoints}To${Capitalize<keyof typeof breakpoints>}`
  | keyof typeof breakpoints
  | 'base'
>;

const adjust = (value: number) => value - 1;
const capitalize = (str: string) => str.charAt(0).toUpperCase() + str.slice(1);

const getPermutations = <T extends string>(breakpoints: T[]): [T, T][] => {
  const result: [T, T][] = [];
  for (let i = 0; i < breakpoints.length; i++) {
    for (let j = i + 1; j < breakpoints.length; j++) {
      result.push([breakpoints[i], breakpoints[j]]);
    }
  }
  return result;
};

const bpValues = Object.fromEntries(
  Object.entries(breakpoints).map(([key, value]) => [key, parseInt(value)]),
) as Record<keyof typeof breakpoints, number>;

const bpKeys = Object.keys(bpValues) as Array<keyof typeof bpValues>;

const buildMediaQuery = ({ min, max }: { min?: number; max?: number }) => {
  if (min && max) return `(min-width: ${min}px) and (max-width: ${max}px)`;
  if (min) return `(min-width: ${min}px)`;
  if (max) return `(max-width: ${max}px)`;
  return '';
};

const getOnly = (name: keyof typeof bpValues) => {
  const currentIndex = bpKeys.indexOf(name);
  const nextBp = bpKeys[currentIndex + 1];

  if (nextBp) {
    return buildMediaQuery({ min: bpValues[name], max: adjust(bpValues[nextBp]) });
  }
  return buildMediaQuery({ min: bpValues[name] });
};

const createMediaQueryMapper = () => {
  const permutations = getPermutations(bpKeys);

  const values = bpKeys
    .flatMap((name) => {
      const value = bpValues[name];

      const down: [string, string] = [`${name}Down`, buildMediaQuery({ max: adjust(value) })];
      const up: [string, string] = [name, buildMediaQuery({ min: value })];
      const only: [string, string] = [`${name}Only`, getOnly(name)];

      return [up, only, down];
    })
    .filter(([_, value]) => value !== '')
    .concat(
      permutations.map(([min, max]) => {
        const minValue = bpValues[min];
        const maxValue = bpValues[max];
        return [`${min}To${capitalize(max)}`, buildMediaQuery({ min: minValue, max: adjust(maxValue) })];
      }),
    );

  return Object.fromEntries(values) as Record<Exclude<BreakpointConditions, 'base'>, string>;
};

export const mediaQueryMapper = createMediaQueryMapper();
